/* eslint-disable @typescript-eslint/no-explicit-any */
import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import { useTableCreatorPositionInfo } from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleGroupShellReturn {
  clearSubGroupShellData: () => void;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: DealLaScSection;
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    subGroupShellIndex: number;
    subGroupShellKey: string | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useClearDealLaScKeyCommentSubGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    subGroupShellIndex,
    subGroupShellKey,
    tableKey,
    tableIndex,
  } = positionPropsInfo;

  const articleSection = ENTRY_DATA_SECTION_KEYS.dealsLaSc;
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { changeFocusStatus } = useTableCreatorFocusStatus();
  const { info } = useTableCreatorPositionInfo();

  const clearSubGroupShellData = () => {
    if (info.subGroupShellKey !== "Key Comment List") return;

    const keyCommentListLength =
      entryData?.[articleSection]?.["Key Comment List"]?.length || 0;

    const clearKeyCommentListSubGroupShell = (): ArticleEntryData =>
      ({
        ...entryData,
        [articleSection]: {
          ...entryData[articleSection],
          ...(info.subGroupShellIndex === 0
            ? {
                "Key Comment": {
                  ...entryData[articleSection]?.["Key Comment"],
                  Quote: {
                    ...entryData[articleSection]?.["Key Comment"]?.Quote,
                    value: [],
                  },
                  Spokesperson: {
                    ...entryData[articleSection]?.["Key Comment"]?.Spokesperson,
                    value: [],
                  },
                } as any,
              }
            : {}),
          "Key Comment List": entryData[articleSection]?.[
            "Key Comment List"
          ]?.map((item, idx) => {
            if (idx === info.subGroupShellIndex) {
              return {
                ...item,
                "Comment Quote": { ...item["Comment Quote"], value: [] },
                "Comment Spokesperson": {
                  ...item["Comment Spokesperson"],
                  value: [],
                },
              };
            }
            return item;
          }),
        },
      } as ArticleEntryData);

    setEntryData(clearKeyCommentListSubGroupShell());
    changeCurrentHistory({
      entryData: clearKeyCommentListSubGroupShell(),
      changedDataInfo: {
        target: "subGroupShell",
        action: "clear",
        position: {
          groupShellKey,
          groupTableIndex,
          tableKey,
          tableIndex,
          groupTableKey,
          groupShellIndex,
          subGroupShellIndex,
          subGroupShellKey,
          shellKey: null,
          shellIndex: null,
          valueIndex: null,
          value: null,
        },
      },
    });
    changeFocusStatus(
      keyCommentListLength > 1
        ? { type: "delete", container: "subGroupShell" }
        : DEFAULT_FOCUS_STATUS,
    );
  };

  return {
    clearSubGroupShellData,
  };
};
